/* Mounted Maps — render core
   assets/mm-render-core.js

   The render layer, lifted out of renderModel so it can be tuned in one place
   and applied to any crop on earth. It has no opinion about geography: it is
   handed a scene that geometry has already been built into, and it lights,
   frames and captures it.

   Nothing here fetches data, builds geometry, composites onto backplates,
   emails, or touches the cart. Those all stay where they are.

   ---------------------------------------------------------------------------
   INTEGRATION — five edits inside renderModel(), in the order they appear.

   1. After `var renderer = new THREE.WebGLRenderer({...});` and the setSize
      calls, replace the tone-mapping / shadow-type block with:

          MMRender.configureRenderer(THREE, renderer);

   2. Replace the whole `// studio environment for ambient reflection` IIFE
      with:

          MMRender.installEnvironment(THREE, renderer, world);

   3. Replace the HemisphereLight / skyLight / keyLight / fill block with:

          var rig = MMRender.installLights(THREE, world);

      Then wherever the existing code calls `setKey(x, y, z)`, call
      `rig.setKey(x, y, z)` instead. If the painted contact-shadow blob is kept,
      hand it over once with `rig.attachBlob(blob, dropUnits)` so it keeps
      tracking the key light.

   4. Replace the two `new THREE.PerspectiveCamera(...)` set-ups with:

          var studioCam = MMRender.camera(THREE, 'studio', outer);
          var wallCam   = MMRender.camera(THREE, 'wall', outer);

      where `outer` is the existing variable — plate width plus both frame bars.

   5. Leave `shoot()` alone, or swap it for `MMRender.shoot(...)` which does the
      same supersample-and-downscale with the buffer reused between calls.

   Everything else in renderModel is untouched.
   --------------------------------------------------------------------------- */

(function (root) {
  'use strict';

  /* =======================================================================
     TUNING — the whole foundation is these numbers. Change them here and every
     render everywhere changes with them.
     ======================================================================= */
  var T = {
    /* Tone. ACES rolls highlights off instead of clipping them; without it a
       bone-white roof under a strong key goes flat white and every face on the
       building is lost. Raise exposure if the model looks muddy, drop it if
       roofs blow out — in that order, before touching any light. */
    exposure: 1.0,

    /* Environment. A lit room through PMREM, not a painted gradient: it is what
       gives a white surface a different value on each face. envIntensity is how
       much of it the materials pick up. */
    envIntensity: 0.50,
    envRoomBrightness: 0.42,
    envKeyPanel: 5.2,
    envFillPanel: 1.5,
    envFloorBounce: 0.7,

    /* Lights. The balance matters more than any single value: ambient light
       arrives from every direction at once, so a generous environment or a wide
       soft sky light is what flattens a block of buildings into one grey mass.
       Keep the key strong and the fills mean. */
    /* Build 4.3: the key sits LOWER and further out than it did (was radius
       9.0, height 9.5 -- about 47 degrees, near noon). A low sun is what gives
       an object on a desk its length of shadow, and length of shadow is most of
       what makes a model read as a physical thing rather than a diagram. */
    keyRadius: 10.2,
    keyHeight: 6.6,
    /* Straight down needs its own, lower again: that pane has nothing but
       shadow to separate one roof from the next. */
    topKey: [-7.0, 5.0, -5.0],
    keyIntensity: 2.62,
    keyColour: 0xFFF2DC,
    keyShadowMap: 4096,
    keyShadowRadius: 6,        // Build 1: soft, studio-like edges
    keyShadowBlur: 16,

    /* Straight down, blurred wide — a stand-in for ambient occlusion. It is not
       AO and cannot produce contact darkening, so it stays modest. Turning this
       up is what washes the model out. */
    /* Build 4.3: that is no longer true. This blurred straight-down light was
       the ONLY contact darkening in the scene while the AO pass was broken, so
       it had to be carried high and it flattened everything it touched. Build
       4.0 gave us real occlusion, so this can come down to what it should have
       been all along -- a soft sky, not a fake AO. */
    skyIntensity: 0.60,
    skyColour: 0xE9EEF5,
    skyShadowRadius: 34,

    fillIntensity: 0.09,
    fillColour: 0xE8EDF5,
    hemiIntensity: 0.075,

    /* Cameras. A long lens: wide angle is an instant tell that something is a
       render rather than a photograph. */
    studioFov: 26,
    studioFill: 0.70,          // plate width as a fraction of the frame
    studioDir: [-0.31, 0.56, 0.77],
    studioKey: [-7.5, 9.5, 2.5],

    wallFov: 22,
    wallFill: 0.92,
    wallKey: [-7.5, 6.2, -5.2],

    /* Capture. Drawing at the size it is displayed leaves every edge to MSAA,
       which does nothing for the things that actually look rough here — the
       shading step along a roof, a sub-millimetre road, the speckle of trees.
       Draw larger, average down. */
    superSampleCeilingDesktop: 4096,
    superSampleCeilingMobile: 3072,
    superSampleMax: 2,

    /* Ambient occlusion. Radius is in pixels at a 1600 px render and scales
       with the buffer, so the darkening covers the same amount of MODEL at
       every output size rather than spreading as the image grows. Intensity is
       how dark the deepest crevice goes; bias trims self-shading on flat
       ground, which otherwise puts a grey haze over the whole plate. */
    /* OFF by default. The pass renders the scene to a target and composites it
       back, and when that goes wrong it does not throw -- it hands back an
       empty buffer, which arrives as a render with the backplate photos present
       and no model on them. That is a worse failure than having no ambient
       occlusion at all, because it looks like the whole render broke.

       aoIntensity is the switch (0 = off), and nothing else in the model
       depends on it. See the Build 2.1 note below before turning it on. */
    aoRadiusPx: 26,
    /* Build 4.0: ON, and the two faults that kept it off are fixed.
       1. The offscreen target used to be 8-bit and sRGB-encoded, and the
          composite wrote it straight to the canvas -- so the ACES curve never
          ran and every highlight clipped. That is what made it dark and
          oversaturated. The target is now linear half-float and the composite
          applies tone mapping and sRGB exactly once, at the end.
       2. The surface normal is inferred from depth, which is wrong across a
          silhouette -- the roofline halos. Samples further than aoMaxJump in
          view depth are now a different surface, not an occluder.
       Raise aoIntensity for heavier contact shading, 0 to switch the pass off
       entirely. Nothing else in the model depends on it. */
    aoIntensity: 0.5,
    aoBias: 0.035,
    /* View-space depth, in plate units (the plate is 10 across at every size,
       so this is scale-independent: 0.5 = 5% of the model's diameter). */
    aoMaxJump: 0.5,

    /* ---- Build 4.1: the grade ----------------------------------------------
       Applied in the same pass as the occlusion, in LINEAR light, immediately
       before the ACES curve -- which is where a grade belongs: after this the
       tone curve rolls the highlights off and nothing clips.
       The render is a photograph of an object, not a map, and a photograph
       gets graded. Every number here is deliberately small; the point is that
       the model reads as a physical thing on a desk, not that it looks
       processed. gradeOn false renders exactly as Build 4.0 did. */
    gradeOn: true,
    /* S-curve strength about linear mid grey. Above 1 deepens the shadow side
       of every building and lets the lit roofs separate from the ground. */
    gradeContrast: 1.08,
    /* Toward daylight. Warms the bone and the tan, cools nothing visibly --
       the same shift a warm key light would give, without moving the light. */
    gradeWarmth: 0.025,
    /* Overall saturation, then a second lift weighted to LOW-chroma pixels, so
       the near-neutral bone and tan gain the most and the greens do not go
       lurid. This is vibrance, not saturation. */
    gradeSaturation: 1.04,
    gradeVibrance: 0.18,
    /* Hue-targeted, because these are the two materials a customer judges the
       print by: the grass and the water. Both are printed in one flat filament,
       so on the real model they read stronger than a literal render makes them. */
    gradeGreenLift: 0.14,
    gradeWaterLift: 0.12,

    /* ---- Build 4.2: tone within a material ---------------------------------
       Kevin, 23 Sep: "when light hits it in real life it really does look like
       it's more colourful". He is right, and it is the render that is wrong.
       A real print has variation this renderer throws away -- every building
       faces the key differently, a roof faces the sky while a wall faces
       sideways, layer lines throw micro-shadows, and white PLA bounces light
       into its own corners. One flat colour per material shows LESS than the
       object does in the hand.
       So: variation that stands in for light is pushed. Variation that would
       imply a second material on the plate is not. That is why the roof is the
       same colour as the wall, only brighter -- never a different colour. */
    /* Per-building lightness, +/- this fraction. The fix for the straight-down
       shot, where a hundred identical roofs merge into one white field. */
    bldVariation: 0.055,
    /* How much brighter an up-facing face is than a side-facing one. This is
       the sky, not a second filament. */
    roofBoost: 1.055,
    /* Ground: a low-frequency wander so a field is not one flat fill... */
    groundVariation: 0.1,
    /* ...and lightness that follows the relief, so terraces have form. */
    groundReliefTone: 0.1,
    /* Trees: size and colour spread. Uniform canopies read as broccoli. */
    treeVariation: 1.0,

    /* ---- Build 4.4: terrace risers ----------------------------------------
       A riser is a vertical face, so it catches far less of the key than the
       surface above it -- and after Build 4.3 dropped the sun, less again. On
       a gently sloping field you get dozens of them, and they stop reading as
       steps in the ground and start reading as dark lines drawn on it.
       The object does not do that: on the print the riser is the same filament
       as the surface, 0.5 mm high, and it reads as a step. So the riser's
       albedo is lifted to compensate for the light it cannot receive. Set to 1
       to go back to a riser the same colour as its surface. */
    riserLift: 1.2,

    /* Build 4.5: city walls. Real height in metres, run through the same lift
       as a building, with a floor in millimetres so a wide crop or a small
       plate cannot bury it. A walled town is bought because of the wall. */
    cityWallM: 14,
    cityWallMinMm: 2.4,

    /* ---- physical proportions, in real millimetres of the finished model ----
       These are absolute, so they hold at every size: 9 mm of frame is 9 mm on
       a 15 cm model and on a 40 cm one. Nothing here is a fraction of the
       plate, which is what stopped the small size looking chunky. */
    frameWidthMm: 3,    // Build 1: slim round ring
    /* Negative on purpose. A positive lip drops the map into a well, and at a
       three-quarter camera the far frame wall then stands in front of the back
       of the plate and hides map that was rendered. Below the map surface, the
       model stands proud of its frame the way the reference does. */
    /* Positive now, and safe because the frame is measured from the LOWEST
       ground rather than the mean: it sits level with the shore, so the water
       -- which is laid a little under a millimetre above the ground -- passes
       beneath it and the frame stands visibly above the waterline all the way
       round, as the reference does. Hills still rise clear of it. */
    frameLipMm: 0.6,    // ring top this far above the LOWEST land, on every model
    /* Real thickness of the piece, and it no longer changes with the terrain.
       It used to be padded out by the height of the tallest hill, so 4 mm was
       fine while that padding existed and became a wafer the moment it went. */
    baseThicknessMm: 6,
    /* Build 1: terrain is cut in steps of this height on the model, the same
       on every size and place, so flat ground still shows fine layer lines. */
    stepMm: 0.5,

    /* ---- terrain ----
       reliefStrength must never be absent-means-zero. When it was read as
       `cfg.reliefStrength || 0`, an unset theme setting switched terrain off
       entirely — flat plate, and no contour lines, since those only draw where
       there is relief to trace. */
    reliefStrength: 1,
    /* Quantise terrain into bands. Smooth terrain gives a soft hillside with
       nothing to catch the light; the stepped terraces are the reference's
       signature and they are what make a slope read as a printed object rather
       than a moulded one. */
    /* Bands the terrain is quantised into. Fewer means chunkier risers: at 14 mm
       of relief, 12 bands gives roughly 1.2 mm per step, which is enough for
       each one to catch light and cast a small shadow. At 18 the steps were
       under a millimetre and the layering read as drawn lines rather than as
       carved depth. This is the half of the effect that makes the shape; the
       contour lines below are the half that draws its edge. */
    /* Bands the terrain is quantised into. The ground is built as flat cells
       with vertical risers between them, which is what gives the layered,
       carved look. It does show the mesh grid on very gentle country -- the
       step edges follow the cells rather than the contours -- but that reads
       better than a smooth surface with no layering at all. Raise for finer
       bands, lower for deeper ones; 0 switches to a smooth surface. */
    /* Bands the terrain is cut into. Thin and many, not thick and few: on the
       reference the layering reads as a fine grain following the shoreline and
       the slope, closer to a contour map cut in relief than to a staircase.
       At 50 mm of relief, 42 bands is a little over a millimetre a step.

       Raising this alone is not enough -- STEP_GRID in the configurator has to
       rise with it, or the cells become too coarse to cut a thin band cleanly
       and the steep ground silently loses its terracing. */
    contourSteps: 42,
    /* Vertical exaggeration applied to whatever the ground actually does.
       Proportional, not normalised: a flat city should look flat and a mountain
       should dominate. Aiming every crop at one fixed depth gave Dublin hills
       it does not have and squeezed Dubrovnik's 190 m into the same height as
       Dublin's 20. */
    reliefExaggeration: 1.25,   // Build 1.1
    /* Bounds in real millimetres on the finished model. The floor stops a
       genuinely flat crop arriving as a sheet of card; the ceiling stops an
       alpine one becoming a spike taller than its own frame. */
    minReliefMm: 4,
    maxReliefMm: 16,    // Build 1.1: soft ceiling on TERRAIN only, never on buildings

    /* Compression curve on the height, applied across the crop's own range.

       Below 1 it lifts the low ground and squeezes the top. Straight
       proportional relief spends nearly all of a mountainous crop's height on
       the mountain, leaving the town at its foot flattened into the bottom few
       per cent where nothing reads — and the town is the part someone bought.

       0.6 keeps a hillside's shape while stopping a summit dominating.
       1 = the old straight line. Lower = flatter peaks, more visible lowland. */
    reliefCurve: 0.6,        // ceiling, so an alpine crop is not a spike

    /* Vertical exaggeration on buildings. At 1:5000 a two-storey house is about
       1.2 mm on a 20 cm plate -- below what the printer resolves and well below
       what the eye separates from the ground. Every relief model exaggerates;
       2.5 left villages reading as scattered chips. The taper in the builder
       still stops a tall block becoming a tower. */
    heightExaggeration: 1.9,   // Build 1: applied to the first 12 m of a building only

    /* Two-tone buildings: roof one colour, walls another. The single clearest
       difference between our buildings and the reference's. With one colour top
       and side, a cluster of houses merges into a pale smudge — the only thing
       separating them is shading, and at 1:5000 the shading is tiny.

       PRODUCTION NOTE: this implies the roof is a second material on the plate.
       Set to false if the colourway must stay at its current material count. */
    twoToneBuildings: false,
    roofColour: 0xAAA9A3,

    /* Scrub, heath and moor. On a Mediterranean or karst hillside that is most
       of the ground, and painting it meadow green is what makes a Dalmatian
       coast read as Northern European. null falls back to the grass colour. */
    scrubColour: 0x9FA477,

    /* Trees across open green ground, not only inside mapped woodland. OSM
       surveys woods and street trees; nobody surveys the scrub on a hillside,
       so a model built from mapped trees alone leaves most of the countryside
       bare while the reference has continuous cover. This puts trees on land
       the model is already colouring as vegetation — no new claim, just cover
       on ground already shown as covered — and it is deterministic from
       position, so a crop plants the same trees every time and the preview
       matches the print. false = surveyed trees only. */
    wildTrees: true,
    wildTreeSpacingM: 17,

    /* One green per kind of land, in the order [parks, farmland, meadow,
       rough ground]. The height steps in the builder were carrying this alone
       and a third of a millimetre is not something the eye reads across a whole
       plate — so farmland, pasture and scrub all arrived as one sheet. Distinct
       shades are most of what stops open country reading as a blank.

       PRODUCTION NOTE: like the roofs, more greens means more materials. Set to
       null to fall back to a single grass colour. */
    /* null = one green everywhere. Different greens per land type worked, but
       it made the plate read as a coloured map rather than a printed object,
       and it multiplied the material count. The reference distinguishes its
       fields by TEXTURE instead -- rows cut into the ground, parcel edges --
       which is a thing a single-material print can actually do. Set to an array
       of four to go back to tinting. */
    grassTones: null,

    /* Drawn contour ribbons, as distinct from the terracing above. Off: the
       terraces are geometry and read as a printed object; drawn lines read as
       pen marks on a map, and across open farmland they are the only thing on
       the plate. The reference has terraces everywhere and lines nowhere. */
    /* Off. These are ribbons drawn on the surface, and with the terracing
       working they compete with it: the steps make real depth and the lines lay
       a scattered pen drawing over the top of it. Depth reads better on its own.
       contourSteps above is the setting that carries the layered look. */
    contourLines: false,

    /* Ceiling on heights that were GUESSED rather than tagged. A mast tagged
       building=tower would otherwise infer 77 m and render as a needle. */
    maxInferredHeightM: 22,

    /* Trees come from natural=tree nodes and from scattering inside wood,
       forest, scrub and park polygons -- OSM maps a wood as an outline, so
       without the scatter a forest is a flat green sheet. */
    trees: true,

    /* Key light positions per camera. Lower than they were: shadow length is
       most of what tells you a block has height at 1:5000, and at the old
       angle the shadows were barely longer than the buildings. */
    studioKey: [-7.5, 9.5, 2.5],   // Build 1: higher key, shorter shadows
    /* Build 3: lower and more to the side. The wall shot now has a wall behind
       the piece, and a light almost straight overhead put the shadow directly
       underneath it, where nothing can see it. At this angle the shadow steps
       out from under the frame and onto the wall. */
    wallKey: [-7.5, 6.2, -5.2],
  };

  /* =======================================================================
     Renderer
     ======================================================================= */
  function configureRenderer(THREE, renderer) {
    renderer.shadowMap.enabled = true;
    /* VSM blurs in shadow space, which gives the wide soft penumbra a product
       shot has. PCF only softens by a texel or two. */
    renderer.shadowMap.type = THREE.VSMShadowMap || THREE.PCFSoftShadowMap;
    if ('outputColorSpace' in renderer && THREE.SRGBColorSpace) {
      renderer.outputColorSpace = THREE.SRGBColorSpace;
    } else if ('outputEncoding' in renderer && THREE.sRGBEncoding) {
      renderer.outputEncoding = THREE.sRGBEncoding;
    }
    if (THREE.ACESFilmicToneMapping) {
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = T.exposure;
    }
    return renderer;
  }

  /* =======================================================================
     Environment

     A small lit room run through PMREMGenerator. PMREMGenerator is core
     Three.js, so this needs no addon imports and works with the UMD build.

     What this replaces — a 512x256 canvas with a gradient and three white
     rectangles — reflected as a flat smear, because it was flat. A real
     environment is most of the difference between plastic that looks lit and
     plastic that looks painted.
     ======================================================================= */
  function installEnvironment(THREE, renderer, scene) {
    var env = new THREE.Scene();
    var geo = new THREE.BoxGeometry();
    if (geo.deleteAttribute) geo.deleteAttribute('uv');

    function panel(x, y, z, sx, sy, sz, intensity, inward) {
      var mat = new THREE.MeshBasicMaterial({
        side: inward ? THREE.BackSide : THREE.FrontSide
      });
      /* Colour is not clamped to 1 in Three, so a panel can be genuinely
         brighter than white — which is what makes it read as a light source
         rather than a pale wall. */
      mat.color.setScalar(intensity);
      var m = new THREE.Mesh(geo, mat);
      m.position.set(x, y, z);
      m.scale.set(sx, sy, sz);
      env.add(m);
    }

    panel(0, 0, 0, 24, 14, 24, T.envRoomBrightness, true);   // the room itself
    panel(-5, 6.4, -2.5, 7, 0.1, 7, T.envKeyPanel, false);   // key softbox
    panel(5.5, 6.2, 3, 6, 0.1, 6, T.envFillPanel, false);    // opposite fill
    panel(0, -6.4, 0, 20, 0.1, 20, T.envFloorBounce, false); // floor bounce

    var pm = new THREE.PMREMGenerator(renderer);
    if (pm.compileEquirectangularShader) pm.compileEquirectangularShader();
    var rt = pm.fromScene(env, 0.04);
    scene.environment = rt.texture;
    pm.dispose();

    env.traverse(function (o) { if (o.material) o.material.dispose(); });
    geo.dispose();
    return rt.texture;
  }

  /* =======================================================================
     Lights
     ======================================================================= */
  function installLights(THREE, scene, opts) {
    opts = opts || {};
    var extent = opts.extent || 8.4;   // half-width the shadows must cover

    /* A hemisphere rather than a flat ambient: it carries a sky-to-ground
       gradient, so vertical faces darken toward their base. Not real ambient
       occlusion, but it buys some of what AO buys for no extra passes. */
    var hemi = new THREE.HemisphereLight(0xffffff, 0xA79E8C, T.hemiIntensity);
    scene.add(hemi);

    var sky = new THREE.DirectionalLight(T.skyColour, T.skyIntensity);
    sky.position.set(0.01, 40, 0.01);
    sky.castShadow = true;
    sky.shadow.mapSize.set(2048, 2048);
    sky.shadow.camera.left = -extent;
    sky.shadow.camera.right = extent;
    sky.shadow.camera.top = extent;
    sky.shadow.camera.bottom = -extent;
    sky.shadow.camera.near = 1;
    sky.shadow.camera.far = 90;
    sky.shadow.radius = T.skyShadowRadius;
    sky.shadow.blurSamples = 24;
    sky.shadow.bias = -0.0002;
    scene.add(sky);

    var key = new THREE.DirectionalLight(T.keyColour, T.keyIntensity);
    key.castShadow = true;
    key.shadow.mapSize.set(T.keyShadowMap, T.keyShadowMap);
    key.shadow.camera.near = 1;
    key.shadow.bias = -0.00006;
    key.shadow.normalBias = 0.004;
    key.shadow.radius = T.keyShadowRadius;
    key.shadow.blurSamples = T.keyShadowBlur;
    scene.add(key);

    var fill = new THREE.DirectionalLight(T.fillColour, T.fillIntensity);
    scene.add(fill);

    var blob = null, blobDrop = 0;

    /* The shadow frustum has to contain the shadows, not just the model. A tall
       building at a low sun throws several times its own height, and anything
       landing outside the box is simply not drawn — which reads as buildings
       casting nothing. Call this once the tallest thing is known. */
    function fitShadows(tallestUnits) {
      var e = extent + (tallestUnits || 0) * 2.6;
      key.shadow.camera.left = -e;
      key.shadow.camera.right = e;
      key.shadow.camera.top = e;
      key.shadow.camera.bottom = -e;
      key.shadow.camera.far = 60 + (tallestUnits || 0) * 6;
      key.shadow.camera.updateProjectionMatrix();
    }
    fitShadows(0);

    /* Moving the key means the painted contact shadow has to move with it, or
       the two disagree and the whole thing reads as a composite. */
    function setKey(x, y, z) {
      key.position.set(x, y, z);
      fill.position.set(-x * 0.9, Math.abs(y) * 0.62, -z * 0.9);
      if (!blob) return;
      var horiz = Math.hypot(x, z) || 1;
      var drop = blobDrop * (horiz / (y || 1));
      blob.position.x = -(x / horiz) * drop;
      blob.position.z = -(z / horiz) * drop;
    }

    function attachBlob(mesh, dropUnits) {
      blob = mesh;
      blobDrop = dropUnits || 0;
      setKey(key.position.x, key.position.y, key.position.z);
    }

    setKey(T.studioKey[0], T.studioKey[1], T.studioKey[2]);

    return { hemi: hemi, sky: sky, key: key, fill: fill,
             setKey: setKey, attachBlob: attachBlob, fitShadows: fitShadows };
  }

  /* =======================================================================
     Cameras

     Framed so a known fraction of the image is filled by the plate, rather than
     by fitting its diagonal — fitting the diagonal leaves the square itself
     filling barely half the frame, which is what makes a model look far away.
     ======================================================================= */
  function camera(THREE, which, outerWidth) {
    var wall = which === 'wall';
    var fov = wall ? T.wallFov : T.studioFov;
    var fill = wall ? T.wallFill : T.studioFill;
    /* A three-quarter view projects wider than its own width, so it needs more
       room than the straight-down one. */
    var extent = outerWidth * (wall ? 1 : 1.3);
    var dist = extent / (2 * fill * Math.tan((fov / 2) * Math.PI / 180));

    var cam = new THREE.PerspectiveCamera(fov, 1, 0.1, 600);
    if (wall) {
      cam.position.set(0, dist, 0.001);
      cam.lookAt(0, 0, 0);
    } else {
      var d = T.studioDir;
      var len = Math.sqrt(d[0] * d[0] + d[1] * d[1] + d[2] * d[2]);
      cam.position.set(d[0] / len * dist, d[1] / len * dist, d[2] / len * dist);
      cam.lookAt(0, 0, 0);
    }
    cam.userData.mmKey = wall ? T.wallKey : T.studioKey;
    return cam;
  }

  /* =======================================================================
     Capture
     ======================================================================= */
  function planSupersample(renderer, outputPx, isNarrow) {
    var hw = (renderer.capabilities && renderer.capabilities.maxTextureSize) || 4096;
    var ceiling = Math.min(hw, isNarrow ? T.superSampleCeilingMobile
                                        : T.superSampleCeilingDesktop);
    /* Continuous, not half-steps: stepping 2 -> 1.5 -> 1 throws away most of the
       allowance whenever the output size goes up. Take whatever multiple fits. */
    var ss = Math.min(T.superSampleMax, ceiling / outputPx);
    if (ss < 1) ss = 1;
    return { ss: ss, draw: Math.min(Math.round(outputPx * ss), ceiling) };
  }

  var _down = null;
  function shoot(THREE, renderer, scene, cam, outputPx, ss) {
    renderer.render(scene, cam);
    try {
      if (!ss || ss === 1) return renderer.domElement.toDataURL('image/png');
      if (!_down || _down.width !== outputPx) {
        _down = document.createElement('canvas');
        _down.width = _down.height = outputPx;
      }
      var g = _down.getContext('2d');
      /* Cleared first: the model is drawn on transparency, so without this each
         shot composites on top of the one before it. */
      g.clearRect(0, 0, outputPx, outputPx);
      g.imageSmoothingEnabled = true;
      g.imageSmoothingQuality = 'high';
      g.drawImage(renderer.domElement, 0, 0, outputPx, outputPx);
      return _down.toDataURL('image/png');
    } catch (e) {
      if (window.console) console.warn('[mm-render] capture failed:', e.message);
      return null;
    }
  }

  /* =======================================================================
     Materials — one place that decides how every surface responds to light.
     Pass the colour; the response is fixed so it cannot drift per crop.
     ======================================================================= */
  function surface(THREE, colour, o) {
    o = o || {};
    return new THREE.MeshStandardMaterial({
      color: colour,
      roughness: o.roughness != null ? o.roughness : 0.86,
      metalness: 0,
      envMapIntensity: o.envMapIntensity != null ? o.envMapIntensity : T.envIntensity,
      side: o.twoSided ? THREE.DoubleSide : THREE.FrontSide,
      clippingPlanes: o.clippingPlanes || null,
      clipShadows: !!o.clippingPlanes
    });
  }

  /* =======================================================================
     Ambient occlusion

     The one thing a shadow map cannot do. A shadow map answers "is this point
     lit by that light"; ambient occlusion answers "how much of the sky can this
     point see at all", which is what darkens the base of a wall, the slot
     between two buildings across a narrow street, and the ground under a tree
     canopy. Without it a dense block of buildings reads as one pale mass and
     the whole model looks printed onto the plate rather than standing on it.

     Built on core Three: a depth pre-pass into a render target, then a
     full-screen pass that reconstructs view-space position from depth, derives
     a normal from its derivatives, and samples a disc around each pixel. No
     postprocessing addons, so it works with the UMD build loaded by a script
     tag.
     ======================================================================= */
  var AO_FRAG = [
    /* Derivatives recover the surface normal from depth. On WebGL2 they are
       core; on WebGL1 they need the extension, which three adds from
       material.extensions -- never declare it in the source, because an
       #extension line after the first statement is what stopped this shader
       compiling at all until Build 2.1. */
    'precision highp float;',
    'uniform sampler2D tDiffuse;',
    'uniform sampler2D tDepth;',
    'uniform mat4 projectionInverse;',
    'uniform vec2 resolution;',
    'uniform float radius;',
    'uniform float intensity;',
    'uniform float bias;',
    'uniform float maxJump;',
    'uniform float gContrast;',
    'uniform float gWarmth;',
    'uniform float gSat;',
    'uniform float gVib;',
    'uniform float gGreen;',
    'uniform float gWater;',
    'varying vec2 vUv;',
    'float readDepth(vec2 uv){ return texture2D(tDepth, uv).x; }',
    'vec3 viewPos(vec2 uv, float d){',
    '  vec4 clip = vec4(uv * 2.0 - 1.0, d * 2.0 - 1.0, 1.0);',
    '  vec4 v = projectionInverse * clip;',
    '  return v.xyz / v.w;',
    '}',
    /* Per-pixel rotation of the sample ring. Without it every pixel samples the
       same twelve directions and the occlusion bands into visible arcs. */
    'float hash12(vec2 p){',
    '  vec3 q = fract(vec3(p.xyx) * 0.1031);',
    '  q += dot(q, q.yzx + 33.33);',
    '  return fract((q.x + q.y) * q.z);',
    '}',
    /* Build 4.1. Linear in, linear out -- this runs before the tone curve. */
    'vec3 grade(vec3 c){',
    '  c = max(vec3(0.0), (c - 0.18) * gContrast + 0.18);',
    '  c *= vec3(1.0 + gWarmth, 1.0, 1.0 - gWarmth * 0.8);',
    '  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));',
    '  float mx = max(c.r, max(c.g, c.b));',
    '  float mn = min(c.r, min(c.g, c.b));',
    /* Low-chroma pixels get the bigger push: bone and tan lift, the greens
       that are already saturated are left alone. */
    '  float vib = 1.0 + gVib * (1.0 - clamp((mx - mn) * 2.5, 0.0, 1.0));',
    '  float isGreen = clamp((c.g - max(c.r, c.b)) * 4.0, 0.0, 1.0);',
    '  float isBlue  = clamp((c.b - max(c.r, c.g)) * 4.0, 0.0, 1.0);',
    '  float sat = gSat * vib * (1.0 + gGreen * isGreen + gWater * isBlue);',
    '  return max(vec3(0.0), mix(vec3(l), c, sat));',
    '}',
    'void main(){',
    '  vec4 base = texture2D(tDiffuse, vUv);',
    '  float d = readDepth(vUv);',
    '  float occ = 0.0;',
    '  if (d < 0.9999 && base.a > 0.01) {',
    '    vec3 p = viewPos(vUv, d);',
    '    vec3 n = normalize(cross(dFdx(p), dFdy(p)));',
    '    float rot = hash12(gl_FragCoord.xy) * 6.28318530718;',
    '    const int SAMPLES = 16;',
    '    for (int i = 0; i < SAMPLES; i++){',
    '      float fi = float(i);',
    '      float ang = fi * 2.39996323 + rot;',
    '      float rad = radius * sqrt((fi + 0.5) / float(SAMPLES));',
    '      vec2 off = vec2(cos(ang), sin(ang)) * rad / resolution;',
    '      vec2 suv = vUv + off;',
    '      float sd = readDepth(suv);',
    '      if (sd >= 0.9999) continue;',
    '      vec3 sp = viewPos(suv, sd);',
    '      vec3 diff = sp - p;',
    '      float len = length(diff);',
    '      if (len < 1e-5) continue;',
    /* Build 4.0: across a silhouette the depth jumps and the sample belongs to
       a different surface entirely. Counting it is what drew a bright halo
       round every roofline, because the normal guessed from depth is garbage
       exactly there. */
    '      if (abs(diff.z) > maxJump) continue;',
    '      float ndl = max(dot(n, diff / len) - bias, 0.0);',
    /* Falls off with distance, so a building on the far side of a river does
       not shade the near bank. */
    '      float range = 1.0 / (1.0 + len * len * 8.0);',
    '      occ += ndl * range;',
    '    }',
    '    occ = clamp(occ / float(SAMPLES) * intensity, 0.0, 1.0);',
    '  }',
    /* One output path for occluded and background pixels alike: whatever is
       written here goes through tone mapping and the sRGB encode below, and a
       second early return would skip both and leave the sky a different colour
       from the model. */
    '  gl_FragColor = vec4(grade(base.rgb * (1.0 - occ)), base.a);',
    /* The offscreen target is linear half-float, so this is the ONLY place the
       ACES curve and the sRGB encode run -- exactly as they would on a normal
       render straight to the canvas. three substitutes both chunks into a
       ShaderMaterial the same way it does its own shaders. */
    '  #include <tonemapping_fragment>',
    '  #include <colorspace_fragment>',
    '}'
  ].join('\n');

  var AO_VERT = [
    'varying vec2 vUv;',
    'void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }'
  ].join('\n');

  /* ---- AO self-check helpers -------------------------------------------
     The AO pass can fail without throwing. These sample a small block of
     pixels so a silent failure becomes a plain render instead of a blank
     one. Sampling 16x16 rather than the full frame keeps the cost trivial. */
  var AO_PROBE = 16;
  var aoWarned = {};

  function warnOnce(msg) {
    if (aoWarned[msg]) return;
    aoWarned[msg] = true;
    if (window.console) console.warn('[mm-render] ' + msg);
  }

  function readTarget(THREE, renderer, rt) {
    try {
      /* Build 4.0: the target is half-float now, and readRenderTargetPixels
         refuses a buffer whose type does not match. Alpha 0 is still all-zero
         bits in half, so the any-opaque test below reads the same either way. */
      var t = rt.texture && rt.texture.type;
      var buf = (t === THREE.HalfFloatType) ? new Uint16Array(AO_PROBE * AO_PROBE * 4)
              : (t === THREE.FloatType) ? new Float32Array(AO_PROBE * AO_PROBE * 4)
              : new Uint8Array(AO_PROBE * AO_PROBE * 4);
      var x = Math.max(0, ((rt.width  || AO_PROBE) - AO_PROBE) >> 1);
      var y = Math.max(0, ((rt.height || AO_PROBE) - AO_PROBE) >> 1);
      renderer.readRenderTargetPixels(rt, x, y, AO_PROBE, AO_PROBE, buf);
      return buf;
    } catch (e) { return null; }
  }

  function readCanvas(renderer) {
    try {
      var gl = renderer.getContext();
      var buf = new Uint8Array(AO_PROBE * AO_PROBE * 4);
      var x = Math.max(0, (gl.drawingBufferWidth  - AO_PROBE) >> 1);
      var y = Math.max(0, (gl.drawingBufferHeight - AO_PROBE) >> 1);
      gl.readPixels(x, y, AO_PROBE, AO_PROBE, gl.RGBA, gl.UNSIGNED_BYTE, buf);
      return buf;
    } catch (e) { return null; }
  }

  /* null means the probe itself failed -- treat that as inconclusive and let
     the pass through, rather than disabling AO because a read was refused. */
  function anyOpaque(buf) {
    if (!buf) return true;
    for (var i = 3; i < buf.length; i += 4) if (buf[i] > 0) return true;
    return false;
  }

  function makeAO(THREE, renderer, size) {
    if (!THREE.DepthTexture || !THREE.WebGLRenderTarget) return null;
    var rt;
    try {
      /* Build 4.0: half-float and LINEAR. An 8-bit sRGB target clips every
         value above 1.0 and bakes in the gamma, so the ACES curve downstream
         has nothing left to roll off and the composite comes back dark and
         oversaturated. Highlights have to survive the round trip. */
      rt = new THREE.WebGLRenderTarget(size, size, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        type: THREE.HalfFloatType || undefined,
        stencilBuffer: false
      });
      rt.texture.generateMipmaps = false;
      if (THREE.LinearSRGBColorSpace) rt.texture.colorSpace = THREE.LinearSRGBColorSpace;
      rt.depthTexture = new THREE.DepthTexture(size, size);
      rt.depthTexture.type = THREE.UnsignedIntType || THREE.UnsignedShortType;
    } catch (e) {
      if (window.console) console.warn('[mm-render] AO target failed:', e.message);
      return null;
    }

    var mat = new THREE.ShaderMaterial({
      uniforms: {
        tDiffuse: { value: rt.texture },
        tDepth: { value: rt.depthTexture },
        projectionInverse: { value: new THREE.Matrix4() },
        resolution: { value: new THREE.Vector2(size, size) },
        radius: { value: T.aoRadiusPx },
        intensity: { value: T.aoIntensity },
        bias: { value: T.aoBias },
        maxJump: { value: T.aoMaxJump },
        gContrast: { value: T.gradeOn ? T.gradeContrast : 1.0 },
        gWarmth: { value: T.gradeOn ? T.gradeWarmth : 0.0 },
        gSat: { value: T.gradeOn ? T.gradeSaturation : 1.0 },
        gVib: { value: T.gradeOn ? T.gradeVibrance : 0.0 },
        gGreen: { value: T.gradeOn ? T.gradeGreenLift : 0.0 },
        gWater: { value: T.gradeOn ? T.gradeWaterLift : 0.0 }
      },
      vertexShader: AO_VERT,
      fragmentShader: AO_FRAG,
      depthTest: false,
      depthWrite: false,
      blending: THREE.NoBlending
    });
    if (THREE.GLSL3 === undefined) { /* WebGL1: derivatives extension */
      mat.extensions = { derivatives: true };
    } else {
      mat.extensions = { derivatives: true };
    }

    var quadScene = new THREE.Scene();
    var quadCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    quadScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat));

    return {
      /* Returns true if it drew, false if it could not -- the caller falls back
         to a plain render. A shader that fails to compile does so at the first
         draw, not at setup, so this has to be guarded here and not only around
         the constructor. A model that renders without ambient occlusion is a
         worse picture; a model that renders blank is no picture at all. */
      render: function (scene, camera) {
        try {
          var prev = renderer.getRenderTarget();
          renderer.setRenderTarget(rt);
          renderer.clear();
          renderer.render(scene, camera);

          /* Did the scene actually land in the target? A driver that will not
             give us a sampleable depth texture fails here, quietly. */
          if (!anyOpaque(readTarget(THREE, renderer, rt))) {
            renderer.setRenderTarget(prev);
            warnOnce('AO offscreen target came back empty');
            return false;
          }

          renderer.setRenderTarget(prev);
          mat.uniforms.projectionInverse.value.copy(camera.projectionMatrixInverse);
          mat.uniforms.radius.value = T.aoRadiusPx * (size / 1600);
          mat.uniforms.intensity.value = T.aoIntensity;
          mat.uniforms.bias.value = T.aoBias;
          mat.uniforms.maxJump.value = T.aoMaxJump;
          mat.uniforms.gContrast.value = T.gradeOn ? T.gradeContrast : 1.0;
          mat.uniforms.gWarmth.value = T.gradeOn ? T.gradeWarmth : 0.0;
          mat.uniforms.gSat.value = T.gradeOn ? T.gradeSaturation : 1.0;
          mat.uniforms.gVib.value = T.gradeOn ? T.gradeVibrance : 0.0;
          mat.uniforms.gGreen.value = T.gradeOn ? T.gradeGreenLift : 0.0;
          mat.uniforms.gWater.value = T.gradeOn ? T.gradeWaterLift : 0.0;
          renderer.render(quadScene, quadCam);

          /* And did the composite land on the canvas? three.js LOGS a shader
             compile failure rather than throwing, so the draw above can be a
             no-op while reporting success. That is what turns a render into a
             backplate with nothing on it, so it is checked rather than assumed. */
          if (!anyOpaque(readCanvas(renderer))) {
            warnOnce('AO composite drew nothing -- check the console for a THREE.WebGLProgram shader error');
            return false;
          }
          return true;
        } catch (e) {
          if (window.console) {
            console.warn('[mm-render] AO pass failed, rendering without it:', e.message);
          }
          try { renderer.setRenderTarget(null); } catch (e2) {}
          return false;
        }
      },
      dispose: function () {
        try { rt.dispose(); mat.dispose(); } catch (e) {}
      }
    };
  }

  root.MMRender = {
    BUILD: '2026-09-23-B4.5',
    TUNING: T,
    configureRenderer: configureRenderer,
    installEnvironment: installEnvironment,
    installLights: installLights,
    camera: camera,
    planSupersample: planSupersample,
    shoot: shoot,
    makeAO: makeAO,
    surface: surface
  };
})(window);
